import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
    Box, 
    Typography, 
    Button, 
    CircularProgress, 
    Chip,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Tooltip,
    Paper, 
} from '@mui/material';
import { 
    GppBad, 
    WarningAmber, 
    CheckCircleOutline, 
} from '@mui/icons-material';
import api from 'state/axios';


const SEV_COLOR = { ERROR: 'error', WARNING: 'warning' };

const toDateStr = (d) => {
    if (!d) return '';
    if (typeof d === 'string') return d.slice(0, 10);
    return d.toISOString().slice(0, 10);
};

const ViolationCheckDialog = ({
    open,
    onClose,
    startDate,
    endDate,
    employeeIds
}) => {
    const [loading,  setLoading]  = useState(false);
    const [results,  setResults]  = useState(null);   // null = not checked yet
    const [summary,  setSummary]  = useState(null);

    const startStr = toDateStr(startDate);
    const endStr   = toDateStr(endDate);

    const handleCheck = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.post('/assignments/check-violations', {
                startDate: startStr,
                endDate:   endStr,
                ...(employeeIds?.length ? { employeeIds } : {}),
            });
            setResults(res.data.results  || []);
            setSummary(res.data.summary  || {});
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Violation check failed');
        } finally {
            setLoading(false);
        }
    }, [startStr, endStr, employeeIds]);

    const handleClose = () => {
        setResults(null);
        setSummary(null);
        onClose();
    };

    const hasResults = results !== null;
    const clean      = hasResults && results.length === 0;


    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="xl" 
            fullWidth
        >
            <DialogTitle sx={{ pb: 0.5 }}>
                Labor Rule Violations
                <Typography variant="body2" color="text.secondary" mt={0.25}>
                    {startStr} → {endStr}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ minHeight: 320 }}>

                {/* ── Summary chips ── */}
                {summary && (
                    <Box 
                        display="flex" 
                        flexWrap="wrap" 
                        alignItems="center"
                        gap={1.5} 
                        mb={2} 
                    >
                        {clean ? (
                            <Chip
                                icon={<CheckCircleOutline />}
                                label="No violations found"
                                color="success"
                                size="small"
                            />
                        ) : (
                            <>
                                <Chip
                                    icon={<GppBad />}
                                    label={`${summary.errorCount} ERROR${summary.errorCount !== 1 ? 'S' : ''}`}
                                    color={summary.errorCount > 0 ? 'error' : 'default'}
                                    variant={summary.errorCount > 0 ? 'filled' : 'outlined'}
                                    size="small"
                                />
                                <Chip
                                    icon={<WarningAmber />}
                                    label={`${summary.warningCount} WARNING${summary.warningCount !== 1 ? 'S' : ''}`}
                                    color={summary.warningCount > 0 ? 'warning' : 'default'}
                                    variant={summary.warningCount > 0 ? 'filled' : 'outlined'}
                                    size="small"
                                />
                                <Typography variant="body2" color="text.secondary">
                                    across {summary.employeeCount} employee{summary.employeeCount !== 1 ? 's' : ''}
                                    {' · '}{summary.totalRecords} assignment{summary.totalRecords !== 1 ? 's' : ''} affected
                                </Typography>
                            </>
                        )}
                    </Box>
                )}

                {/* ── Results table ── */}
                {hasResults && !clean && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 410 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 130 }}>Employee</TableCell>
                                    <TableCell sx={{ minWidth: 90  }}>Date</TableCell>
                                    <TableCell sx={{ minWidth: 110 }}>Shift</TableCell>
                                    <TableCell sx={{ minWidth: 90  }}>Severity</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>Rule</TableCell>
                                    <TableCell>Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.flatMap((r, ri) =>
                                    r.violations.map((v, vi) => (
                                        <TableRow key={`${ri}-${vi}`} hover>
                                            {vi === 0 && (
                                                <TableCell
                                                    rowSpan={r.violations.length}
                                                    sx={{ verticalAlign: 'top', whiteSpace: 'nowrap', borderRight: '1px solid', borderColor: 'divider' }}
                                                >
                                                    <Typography variant="caption" fontWeight="bold" display="block">
                                                        {r.employeeCode}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {r.employeeName}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            {vi === 0 && (
                                                <TableCell
                                                    rowSpan={r.violations.length}
                                                    sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}
                                                >
                                                    <Typography variant="caption">{r.date}</Typography>
                                                </TableCell>
                                            )}
                                            {vi === 0 && (
                                                <TableCell
                                                    rowSpan={r.violations.length}
                                                    sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}
                                                >
                                                    <Typography variant="caption" display="block" fontWeight={500}>
                                                        {r.shiftName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {r.shiftTime}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Chip
                                                    label={v.severity}
                                                    color={SEV_COLOR[v.severity] || 'default'}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Tooltip title={v.ruleId} placement="top" arrow>
                                                    <Typography variant="caption">{v.name}</Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="caption"
                                                    color={v.severity === 'ERROR' ? 'error' : 'warning.main'}
                                                >
                                                    {v.message}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* ── Empty / idle state ── */}
                {loading && (
                    <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                        <CircularProgress size={36} />
                        <Typography variant="body2" color="text.secondary" ml={2}>
                            Checking assignments…
                        </Typography>
                    </Box>
                )}

                {!loading && !hasResults && (
                    <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                        <Typography variant="body2" color="text.secondary">
                            Click "Check" to scan all assignments in this date range for labor rule violations.
                        </Typography>
                    </Box>
                )}

            </DialogContent>

            <DialogActions>
                <Button 
                    variant="outlined" 
                    color="secondary"
                    onClick={handleClose}
                >
                    Close
                </Button>
                <Button 
                    variant="outlined" 
                    onClick={handleCheck} 
                    disabled={loading}
                >
                    {loading ? 'Checking…' : hasResults ? 'Re-check' : 'Check'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViolationCheckDialog;
